import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { publicAPI } from '../../lib/api';
import { getTenantSlug } from '../../lib/tenant';
import { formatSalary } from '../../lib/salary';
import { MapPin, Briefcase, Wallet, ArrowRight, Search, Building2 } from 'lucide-react';

const SITE = 'https://www.chire.co';

export default function PublicCareersPage() {
  const { companySlug } = useParams();
  const slug = companySlug || getTenantSlug();
  const [jobs, setJobs] = useState([]);
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      publicAPI.jobs(slug).then((r) => r.data || []).catch(() => []),
      publicAPI.careersContext(slug).then((r) => r.data || null).catch(() => null),
    ]).then(([j, ctx]) => {
      if (!alive) return;
      setJobs(j);
      setContext(ctx);
    }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [slug]);

  const isPlatform = !slug; // chire.co/careers — every open role, unbranded
  const branding = isPlatform ? null : context?.branding || null;
  const companyName = isPlatform ? 'Open Roles' : context?.company || branding?.company_name || 'Open Roles';
  const brandColor = branding?.brand_color || '#0076A3';
  const logoUrl = publicAPI.assetUrl(branding?.logo_url);
  const bannerUrl = publicAPI.assetUrl(branding?.banner_url);
  const jobLink = (id) => (slug ? `/${slug}/careers/${id}` : `/careers/${id}`);

  const filtered = jobs.filter((j) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (j.title || '').toLowerCase().includes(s) || (j.location || '').toLowerCase().includes(s) || (j.required_skills || []).some((k) => k.toLowerCase().includes(s));
  });

  const unknownCompany = !loading && !isPlatform && !context?.slug;

  return (
    <div style={{ minHeight: '100vh', background: '#F1EAE1', fontFamily: "'Manrope', 'Inter', sans-serif" }} data-testid="public-careers-page">
      <Helmet>
        <title>{isPlatform ? 'Open Roles — CHIRE Careers' : `${companyName} — Careers`}</title>
        <meta name="description" content={isPlatform ? 'Browse every open role hiring on CHIRE. Apply in under a minute.' : `Browse open roles at ${companyName}. Apply in under a minute.`} />
        <link rel="canonical" href={slug ? `${SITE}/${slug}/careers` : `${SITE}/careers`} />
      </Helmet>

      {bannerUrl ? (
        <div style={{ height: 180, backgroundImage: `url(${bannerUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }} data-testid="careers-banner" />
      ) : null}

      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.5rem', background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #ECE7DE', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {isPlatform ? (
            <Link to="/" data-testid="careers-chire-logo"><img src="/chire-logo-full.png" alt="CHIRE" style={{ height: 46, objectFit: 'contain', display: 'block' }} /></Link>
          ) : logoUrl ? (
            <img src={logoUrl} alt={companyName} style={{ height: 32, width: 'auto', maxWidth: 140, objectFit: 'contain' }} data-testid="careers-logo" />
          ) : (
            <span style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontWeight: 800, fontSize: '1.25rem', color: brandColor, letterSpacing: '-0.02em' }}>{companyName}</span>
          )}
        </div>
        {!isPlatform && <Link to="/" style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#475569', textDecoration: 'none' }}>Powered by CHIRE</Link>}
      </header>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '2.5rem 1.5rem 4rem' }}>
        <h1 data-testid="careers-company-title" style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '2.25rem', fontWeight: 700, color: '#002A38', margin: 0 }}>
          {companyName === 'Open Roles' ? 'Open Roles' : `Careers at ${companyName}`}
        </h1>
        {branding?.blurb ? (
          <p style={{ fontSize: '1rem', color: '#475569', marginTop: 10, maxWidth: 640 }}>{branding.blurb}</p>
        ) : null}
        {!loading && (isPlatform || context?.slug) ? (
          <p data-testid="careers-role-count" style={{ fontSize: '1rem', color: '#64748B', marginTop: 8 }}>{jobs.length} role{jobs.length !== 1 ? 's' : ''} hiring now.</p>
        ) : null}

        {unknownCompany ? (
          <div style={{ textAlign: 'center', padding: '4rem 1rem', color: '#94A3B8' }} data-testid="careers-no-company">
            <Building2 size={40} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
            <div style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontWeight: 700, color: '#64748B' }}>Company not found</div>
            <Link to="/careers" data-testid="careers-browse-all-link" style={{ display: 'inline-block', fontSize: '0.875rem', marginTop: 8, color: '#0076A3', fontWeight: 600 }}>Browse all open roles →</Link>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '1.5rem 0 2rem', padding: '0.7rem 1rem', background: '#fff', border: '1px solid #ECE7DE', borderRadius: 999, maxWidth: 420 }}>
              <Search size={16} color="#94A3B8" />
              <input data-testid="careers-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, location or skill" style={{ flex: 1, border: 'none', outline: 'none', fontSize: '0.875rem', color: '#002A38', background: 'transparent' }} />
            </div>

            {loading ? (
              <div style={{ color: '#64748B' }}>Loading roles…</div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '4rem 1rem', color: '#94A3B8' }} data-testid="careers-no-roles">
                <Briefcase size={40} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                <div style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontWeight: 700, color: '#64748B' }}>{jobs.length === 0 ? 'No open roles right now' : 'No roles match your search'}</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '0.85rem' }}>
                {filtered.map((job) => {
                  const sal = formatSalary(job);
                  return (
                    <Link key={job.id} to={jobLink(job.id)} data-testid="careers-job-card" style={{ textDecoration: 'none', display: 'block' }}>
                      <div style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.25rem 1.5rem', transition: 'border-color 0.15s, box-shadow 0.15s', display: 'flex', alignItems: 'center', gap: '1rem' }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#BBE3F5'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(240, 80, 0,0.08)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#ECE7DE'; e.currentTarget.style.boxShadow = 'none'; }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '1.0625rem', fontWeight: 700, color: '#002A38' }}>{job.title}</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 6, fontSize: '0.8125rem', color: '#64748B' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><MapPin size={13} /> {job.location || 'Remote'}</span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, textTransform: 'capitalize' }}><Briefcase size={13} /> {job.job_type}</span>
                            {sal && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#0F766E', fontWeight: 700 }}><Wallet size={13} /> {sal}</span>}
                          </div>
                        </div>
                        <ArrowRight size={18} color={brandColor} style={{ flexShrink: 0 }} />
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
